// LoginModal — modální dialog pro přihlášení.
// Volá POST /api/auth/login, uloží token+user do useAuthStore.
// Obsahuje i odkaz "Zapomenuté heslo?" → přepne na formulář pro vyžádání resetovacího e-mailu,
// a odkaz "Zaregistrovat se" → /register.
//
// Řízení viditelnosti je globální přes useLoginModalStore (show/hide). Komponenta se
// vykresluje jednou v App.jsx; když `open` je false, nevrací nic.
// Po vypršení přihlášení (reason 'session_expired') ukazuje nad formulářem hlášku a zavření
// bez přihlášení přepne aplikaci na anonyma (useAuthStore.dismissSessionExpired).
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../../api/auth'
import { trackView } from '../../hooks/useTrackProduct'
import { trackFlush } from '../../api/usageApi'
import { useAuthStore } from '../../store/useAuthStore'
import { useDeployConfigStore } from '../../store/useDeployConfigStore'
import { useLoginModalStore } from '../../store/useLoginModalStore'
import { ModalOverlay } from '../ModalOverlay'

// ─── Formulář zapomenutého hesla ────────────────────────────────────────────────
function ForgotPasswordForm({ onBack, onClose, initialLogin = '' }) {
  const [login, setLogin]     = useState(initialLogin)
  const [error, setError]     = useState(null)
  const [sent, setSent]       = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await authApi.forgotPassword(login)
      setSent(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="flex flex-col gap-5">
        <h2 className="text-xl font-bold text-gray-800">Zapomenuté heslo</h2>
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Pokud účet existuje, byl odeslán e-mail s odkazem pro nastavení nového hesla.
        </p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
          >
            Zavřít
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-xl font-bold text-gray-800">Zapomenuté heslo</h2>
      <p className="text-sm text-gray-500">
        Zadejte uživatelské jméno nebo e-mail — pošleme vám odkaz pro nastavení nového hesla.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600 font-medium">Uživatelské jméno nebo e-mail</label>
          <input
            type="text"
            autoFocus
            value={login}
            onChange={e => setLogin(e.target.value)}
            required
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end mt-1">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Zpět
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Odesílám…' : 'Odeslat odkaz'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── Vnitřní obsah přihlášení ───────────────────────────────────────────────────
function LoginContent({ onClose, notice }) {
  const navigate = useNavigate()
  const setAuth = useAuthStore(s => s.setAuth)
  const registrationEnabled = useDeployConfigStore(s => s.registrationEnabled)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState(null)
  // 'pending' | 'password_change_required' | null — speciální stav (403 se zvláštní hláškou)
  const [special, setSpecial]   = useState(null)
  const [resendSent, setResendSent] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [showForgot, setShowForgot] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setSpecial(null)
    setResendSent(false)
    setLoading(true)
    try {
      const { token, user } = await authApi.login(username, password)
      setAuth(token, user)
      // Sledování přístupů (etapa K2) — přihlášení je systémová část bez uzlu v registru.
      // Odesílá se hned, aby dávka odešla s tokenem nového uživatele, ne až po dalším kliknutí.
      trackView('auth', 'login')
      trackFlush()
      onClose()
    } catch (err) {
      if (err.error === 'account_pending') {
        setSpecial('pending')
        setError(err.message || 'Účet čeká na schválení administrátorem.')
      } else if (err.error === 'password_change_required') {
        setSpecial('password_change_required')
        setError(err.message || 'Účet čeká na nastavení hesla.')
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    try {
      await authApi.forgotPassword(username)
      setResendSent(true)
    } catch {
      setResendSent(true) // generická odpověď — nezáleží
    }
  }

  if (showForgot) {
    return <ForgotPasswordForm onBack={() => setShowForgot(false)} onClose={onClose} initialLogin={username} />
  }

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-xl font-bold text-gray-800">Přihlášení</h2>

      {notice && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {notice}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600 font-medium">Uživatelské jméno nebo e-mail</label>
          <input
            type="text"
            autoComplete="username"
            placeholder="jméno nebo e-mail"
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600 font-medium">Heslo</label>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex flex-col gap-2">
            <span>{error}</span>
            {special === 'password_change_required' && !resendSent && (
              <button
                type="button"
                onClick={handleResend}
                className="self-start text-blue-600 hover:text-blue-800 hover:underline"
              >
                Poslat odkaz pro nastavení hesla
              </button>
            )}
            {special === 'password_change_required' && resendSent && (
              <span className="text-green-700">Odkaz odeslán — zkontrolujte e-mail.</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 -mt-1">
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Zapomenuté heslo?
          </button>
          {registrationEnabled && (
            <button
              type="button"
              onClick={() => { onClose(); navigate('/register') }}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Nemáte účet? Zaregistrujte se
            </button>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Zrušit
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Přihlašuji…' : 'Přihlásit se'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── Globální modál ─────────────────────────────────────────────────────────────
// Vykresluje se jednou v App.jsx; viditelnost řídí useLoginModalStore.
export function LoginModal() {
  const open   = useLoginModalStore(s => s.open)
  const reason = useLoginModalStore(s => s.reason)
  const notice = useLoginModalStore(s => s.notice)
  const hide   = useLoginModalStore(s => s.hide)
  if (!open) return null

  function close() {
    hide()
    if (reason === 'session_expired') useAuthStore.getState().dismissSessionExpired()
  }

  return (
    <ModalOverlay onClose={close} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
        <LoginContent onClose={close} notice={notice} />
      </div>
    </ModalOverlay>
  )
}
